import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './Home.css';
import TechNews from './TechNews';
import LazyImage from './LazyImage';

const Lightbox = ({ images, index, onClose, onPrev, onNext }) => {
  const multi = images.length > 1;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (multi && e.key === 'ArrowLeft') onPrev();
      if (multi && e.key === 'ArrowRight') onNext();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [multi, onClose, onPrev, onNext]);

  return createPortal(
    <div className="lightbox" onClick={onClose} role="dialog" aria-modal="true" aria-label="Expanded image">
      <button className="lightbox-close" onClick={onClose} aria-label="Close image">
        <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
      <figure className="lightbox-figure" onClick={e => e.stopPropagation()}>
        <img src={images[index].src} alt={images[index].alt} className="lightbox-img" />
        <figcaption className="lightbox-caption">{images[index].alt}</figcaption>
      </figure>
      {multi && (
        <>
          <button
            className="lightbox-btn lightbox-btn-prev"
            onClick={e => { e.stopPropagation(); onPrev(); }}
            aria-label="Previous image"
          >
            <svg width="22" height="22" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
            </svg>
          </button>
          <button
            className="lightbox-btn lightbox-btn-next"
            onClick={e => { e.stopPropagation(); onNext(); }}
            aria-label="Next image"
          >
            <svg width="22" height="22" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/>
            </svg>
          </button>
        </>
      )}
    </div>,
    document.body
  );
};

const ProjectCarousel = ({ images }) => {
  const [idx, setIdx] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const multi = images.length > 1;

  const prev = useCallback(() => setIdx(i => (i - 1 + images.length) % images.length), [images.length]);
  const next = useCallback(() => setIdx(i => (i + 1) % images.length), [images.length]);

  return (
    <div className="carousel">
      <div className="carousel-track" style={{ transform: `translateX(-${idx * 100}%)` }}>
        {images.map((img, i) => (
          <div
            key={i}
            className="carousel-slide"
            onClick={() => { setIdx(i); setExpanded(true); }}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setIdx(i); setExpanded(true); } }}
            role="button"
            tabIndex={i === idx ? 0 : -1}
            aria-label={`Expand image: ${img.alt}`}
          >
            <LazyImage src={img.src} alt={img.alt} />
          </div>
        ))}
      </div>
      {multi && (
        <>
          <button className="carousel-btn carousel-btn-prev" onClick={prev} aria-label="Previous image">
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
            </svg>
          </button>
          <button className="carousel-btn carousel-btn-next" onClick={next} aria-label="Next image">
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/>
            </svg>
          </button>
          <div className="carousel-dots">
            {images.map((_, i) => (
              <button
                key={i}
                className={`carousel-dot${i === idx ? ' active' : ''}`}
                onClick={() => setIdx(i)}
                aria-label={`Go to image ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
      {expanded && (
        <Lightbox
          images={images}
          index={idx}
          onClose={() => setExpanded(false)}
          onPrev={prev}
          onNext={next}
        />
      )}
    </div>
  );
};

/* eslint-disable react/jsx-no-comment-textnodes */

const Home = () => {
  // Add floating code elements
  useEffect(() => {
    const addFloatingCode = () => {
      const codeElements = [
        'const magic = true;',
        'function solve() {',
        'return awesome;',
        '}',
        'npm install happiness',
        '// Kansas State pride',
        'while(learning) {',
        'skills++;',
        'console.log("🎉");'
      ];

      const interval = setInterval(() => {
        const element = document.createElement('div');
        element.className = 'code-element';
        element.textContent = codeElements[Math.floor(Math.random() * codeElements.length)];
        element.style.left = Math.random() * 90 + '%';
        element.style.animationDelay = '0s';
        element.style.animationDuration = (10 + Math.random() * 10) + 's';
        
        const codeBg = document.querySelector('.code-bg');
        if (codeBg) {
          codeBg.appendChild(element);
          
          setTimeout(() => {
            if (element.parentNode) {
              element.remove();
            }
          }, 20000);
        }
      }, 3000);

      return () => clearInterval(interval);
    };

    const cleanup = addFloatingCode();
    return cleanup;
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const navbarHeight = 90; // Account for navbar height
      const elementPosition = element.offsetTop - navbarHeight;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
    }
  };

  const [activeFilter, setActiveFilter] = useState('All');

  const projects = [
    {
      id: 7,
      category: 'Professional',
      featured: true,
      label: 'The Home Depot | Software Engineering Internship | Summer 2026',
      title: 'Unified Delivery Management Dashboard',
      description: 'A full-stack web application built with React and TypeScript on a modular monorepo architecture, backed by a Java and Spring Boot service that brings several separate internal systems together in one place. As the sole engineer on the project, I built a landing page that surfaces live daily delivery activity for our Major Appliance delivery facilities, replacing placeholder values with real operational data aggregated across multiple internal sources. The system features a micro-frontend architecture, RESTful APIs with caching and parallel data retrieval, a reusable component library, and comprehensive unit testing - reducing the number of separate tools associates need to check while giving them an at-a-glance view of their facility\'s day to day operations.',
      disclosure: true,
      tags: ['Java', 'Spring Boot', 'React', 'TypeScript', 'Nx Monorepo', 'Micro-Frontends', 'REST APIs', 'Cloud Foundry', 'GitHub', 'Jira', 'IntelliJ', 'Enterprise Architecture'],
      links: [{ href: 'https://www.linkedin.com/posts/evan-jelle_a-month-ago-i-wrapped-up-my-third-consecutive-activity-7500635808010452992-C2bw?utm_source=share&utm_medium=member_desktop&rcm=ACoAAD8Mx8UBjtSv3cU8IqaJW4F_0VF-QiRD5mA', label: 'View LinkedIn Post', icon: 'linkedin' }],
      images: [
        { src: `${process.env.PUBLIC_URL}/SummerInternship25.png`, alt: 'Unified Delivery Management Dashboard' },
        { src: `${process.env.PUBLIC_URL}/AtlantaMDO.jpeg`, alt: 'Atlanta Market Delivery Operations facility' },
        { src: `${process.env.PUBLIC_URL}/KansasCityMDO.jpeg`, alt: 'Kansas City Market Delivery Operations facility' },
        { src: `${process.env.PUBLIC_URL}/WichitaMDO.jpeg`, alt: 'Wichita Market Delivery Operations facility' },
      ],
      imageRight: false,
    },
    {
      id: 1,
      category: 'Professional',
      featured: true,
      label: 'The Home Depot | Software Engineering Internship | Summer 2025',
      title: 'Product Management Automation System',
      description: 'A scalable backend service built with Java and Micronaut that automates product prioritization for promotional events. Working with fellow intern Euan Canoy, we transformed a manual 2-week process into an automated 24-hour solution, enabling merchants to dynamically position key products during sales events. The system features RESTful APIs, PostgreSQL data management, Google Cloud integration, and automated scheduling - reducing operational bottlenecks while enhancing customer discovery and business agility.',
      disclosure: true,
      tags: ['Java', 'Micronaut', 'PostgreSQL', 'Google Cloud', 'REST APIs', 'GitHub', 'Jira', 'IntelliJ', 'Enterprise Architecture'],
      links: [{ href: 'https://www.linkedin.com/posts/evan-jelle_thehomedepot-toolsfornowandlater-thdinterns-activity-7353893471381827584-OneK?utm_source=share&utm_medium=member_desktop&rcm=ACoAAD8Mx8UBjtSv3cU8IqaJW4F_0VF-QiRD5mA', label: 'View LinkedIn Post', icon: 'linkedin' }],
      images: [{ src: `${process.env.PUBLIC_URL}/SummerInternship25.png`, alt: 'Product Management Automation System' }],
      imageRight: true,
    },
    {
      id: 2,
      category: 'Professional',
      featured: false,
      label: 'The Home Depot | Software Engineering Internship | Summer 2024',
      title: 'Analytics Dashboard Modernization System',
      description: 'A scalable backend service built with Go and integrated with Google BigQuery that transforms command-line analytics tools into a modern web application. Working with fellow engineers, I modernized an existing terminal-based system into an accessible REST API with interactive data visualization capabilities. The system features comprehensive metrics tracking, real-time data retrieval from BigQuery, and streamlined analytics workflows - reducing manual command line analytic retrieval processes while enhancing user accessibility and operational efficiency for engineering teams site reliability engineers and other business analytics users.',
      disclosure: true,
      tags: ['Go', 'Google BigQuery', 'REST APIs', 'GitHub', 'Jira', 'Google Cloud Platform', 'React', 'GoLand', 'Enterprise Architecture'],
      links: [],
      images: [{ src: `${process.env.PUBLIC_URL}/SummerInternship24.png`, alt: 'Analytics Dashboard Modernization System' }],
      imageRight: false,
    },
    {
      id: 3,
      category: 'School',
      featured: true,
      label: 'Academic Research Project',
      title: 'Iliff, Colorado: Economic Cycles Digital Humanities Project',
      description: 'An interactive digital humanities project exploring the economic history of my hometown—Iliff, Colorado, a small agricultural community of about 250 people. Created for Kansas State University\'s Digital Humanities course, this project applies computational methods to visualize 120 years of rural economic change (1900-2020). Features include interactive data visualizations tracking population trends with annotated economic events, Logan County sugar beet production data (1910-1985), and business establishment timelines. An event-filtering timeline lets users explore how external forces—railroad expansion, Interstate 76 bypass, and factory closures—shaped this Great Plains town\'s trajectory more than local decisions ever could.',
      disclosure: false,
      tags: ['JavaScript', 'HTML/CSS', 'Data Visualization', 'Interactive Timeline', 'Responsive Design', 'Historical Research', 'GitHub Pages'],
      links: [
        { href: 'https://evan80751.github.io/Digital-Humanities-Project/index.html', label: 'View Live Project', icon: 'external' },
        { href: 'https://github.com/evan80751/Digital-Humanities-Project', label: 'View on GitHub', icon: 'github' },
      ],
      images: [{ src: `${process.env.PUBLIC_URL}/DigitalHumanities.png`, alt: 'Iliff, Colorado: Economic Cycles Digital Humanities Project' }],
      imageRight: false,
    },
    {
      id: 5,
      category: 'School',
      featured: false,
      label: 'CC 410 — Advanced Programming | Kansas State University',
      title: 'Game Grub — Restaurant POS System',
      description: 'A full-featured gaming-themed restaurant Point-of-Sale system built across 12 milestones for CC 410. Manages a 27-item menu (5 entrees, 9 sides, 9 drinks, 4 combos) with a complete tkinter GUI for order management and a Flask/Jinja2 web layer featuring search and filter. Applies OOP principles and the Factory Method design pattern for dynamic combo panels. Rigorously tested with pytest, mypy, flake8, and tox. Motto: "Play fair, eat well."',
      disclosure: false,
      tags: ['Python', 'tkinter', 'Flask', 'Jinja2', 'Bootstrap', 'pytest', 'mypy', 'OOP', 'Factory Method'],
      links: [{ href: 'https://github.com/evan80751/GameGrub---CC410', label: 'View on GitHub', icon: 'github' }],
      images: [
        { src: `${process.env.PUBLIC_URL}/GameGrub.png`, alt: 'Game Grub POS System' },
        { src: `${process.env.PUBLIC_URL}/Milestone12.drawio.png`, alt: 'Game Grub Architecture Diagram' },
      ],
      imageRight: false,
    },
    {
      id: 6,
      category: 'School',
      featured: false,
      label: 'CC 410 — Advanced Programming | Kansas State University | Final Project',
      title: 'Game Vault — Personal Game Library Tracker',
      description: 'A desktop application for tracking a personal video game library across Steam, Xbox, Nintendo, PC, and physical media. Features a live Steam library import via the Steam Web API, manual game management, and a statistics dashboard with backlog scoring, completion rates, and hours-by-genre breakdowns. Implements five design patterns: Singleton (Library), Factory Method (GameFactory), Adapter (SteamClient), Collection Filtering, and Template Method (StatCalculator hierarchy for analytics).',
      disclosure: false,
      tags: ['Python', 'tkinter', 'Steam API', 'requests', 'python-dotenv', 'pytest', 'OOP', 'Design Patterns'],
      links: [{ href: 'https://github.com/evan80751/GameVault---CC410-', label: 'View on GitHub', icon: 'github' }],
      images: [
        { src: `${process.env.PUBLIC_URL}/GameVault.png`, alt: 'Game Vault Library Tracker' },
        { src: `${process.env.PUBLIC_URL}/GameVaultUML.png`, alt: 'Game Vault UML Diagram' },
      ],
      imageRight: true,
    },
    {
      id: 4,
      category: 'Hobby',
      featured: false,
      label: 'Personal Project',
      title: 'Personal Portfolio Website',
      description: 'A modern, responsive portfolio website built with React and featuring a dark theme, animated backgrounds, real-time weather integration, and automated tech news. Showcasing my skills in full-stack development with clean design and a smooth user experience.',
      disclosure: false,
      tags: ['React', 'JavaScript', 'CSS', 'Firebase', 'Weather API', 'Visual Studio Code', 'Responsive Design'],
      links: [],
      images: [{ src: `${process.env.PUBLIC_URL}/portfolio-mockup.png`, alt: 'Personal Portfolio Website' }],
      imageRight: true,
    },
  ];

  const filteredProjects = activeFilter === 'All' ? projects : projects.filter(p => p.category === activeFilter);

  const handleConnectClick = () => {
    // First scroll to contact section
    scrollToSection('contact');
    // Then open email after a short delay
    setTimeout(() => {
      window.location.href='mailto:evanjelleinq@gmail.com?subject=Hello%20Evan&body=Hi%20Evan,%0A%0AI%20found%20your%20portfolio%20and%20would%20like%20to%20connect.';
    }, 800);
  };

  return (
    <div className="home-container">
      {/* Animated code background */}
      <div className="code-bg">
        <div className="code-element" style={{left: '10%', animationDelay: '0s'}}>function() {'{'}</div>
        <div className="code-element" style={{left: '20%', animationDelay: '2s'}}>const dev = new Developer();</div>
        <div className="code-element" style={{left: '30%', animationDelay: '4s'}}>if (coffee.available()) {'{'}</div>
        <div className="code-element" style={{left: '40%', animationDelay: '6s'}}>return code.quality++;</div>
        <div className="code-element" style={{left: '50%', animationDelay: '8s'}}>{'}'}</div>
        <div className="code-element" style={{left: '60%', animationDelay: '10s'}}>git commit -m "✨ magic"</div>
        <div className="code-element" style={{left: '70%', animationDelay: '12s'}}>npm run build</div>
        <div className="code-element" style={{left: '80%', animationDelay: '14s'}}>// TODO: optimize</div>
      </div>

      {/* Sidebar for social actions */}
      <div className="sidebar">
        <div className="sidebar-item" onClick={() => window.open('https://www.linkedin.com/in/evan-jelle/', '_blank')}>
          <svg className="sidebar-icon" fill="currentColor" viewBox="0 0 24 24">
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
          </svg>
          <span className="sidebar-label">LinkedIn</span>
        </div>
        <div className="sidebar-item" onClick={() => window.open('https://github.com/evan80751', '_blank')}>
          <svg className="sidebar-icon" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
          </svg>
          <span className="sidebar-label">GitHub</span>
        </div>
        <div className="sidebar-item" onClick={handleConnectClick}>
          <svg className="sidebar-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
          </svg>
          <span className="sidebar-label">Email</span>
        </div>
        <div className="sidebar-item" onClick={() => window.open('/resume.pdf', '_blank')}>
          <svg className="sidebar-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
          </svg>
          <span className="sidebar-label">Resume</span>
        </div>
      </div>

      {/* Main content */}
      <div className="main-content">
        {/* 1. HOME SECTION */}
        <section id="home" className="hero">
          <div className="hero-content">
            <div className="hero-label">// SOFTWARE ENGINEER</div>
            <h1 className="hero-title">
              Hi, I'm <span className="highlight">Evan Jelle</span>
            </h1>
            <p className="hero-subtitle">Learning every day. Building what matters.</p>
            <p className="hero-description">
              I'm a senior Computer Science student at Kansas State University with a passion for building 
              websites, designing APIs, and working with databases and cloud infrastructure. I'm currently 
              focused on full-stack development because it allows me to combine creativity with technical 
              problem-solving—following ideas from concept to deployment. My recent internship with Home 
              Depot taught me how to navigate real-world systems, collaborate in a professional environment, 
              and build tools that make a meaningful impact.
            </p>
            <div className="cta-group">
              <button onClick={handleConnectClick} className="btn btn-primary">
                Get In Touch
                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/>
                </svg>
              </button>
              <button onClick={() => scrollToSection('projects')} className="btn btn-secondary">View Projects</button>
            </div>
          </div>
          <div className="hero-image">
            <LazyImage
              src={`${process.env.PUBLIC_URL}/profile.jpg`}
              alt="Evan Jelle"
              className="profile-image"
            />
          </div>
        </section>

        {/* Tech Stack */}
        <section className="tech-stack">
          <h2 className="section-title">// TECHNOLOGIES I WORK WITH</h2>
          <div className="tech-grid">
            <div className="tech-category">
              <h3>Frontend</h3>
              <div className="tech-list">
                <span className="tech-item">HTML/CSS</span>
                <span className="tech-item">JavaScript</span>
                <span className="tech-item">React</span>
              </div>
            </div>
            <div className="tech-category">
              <h3>Backend</h3>
              <div className="tech-list">
                <span className="tech-item">Node.js</span>
                <span className="tech-item">Python</span>
                <span className="tech-item">Java</span>
                <span className="tech-item">Spring Boot</span>
                <span className="tech-item">Micronaut</span>
                <span className="tech-item">Go</span>
              </div>
            </div>
            <div className="tech-category">
              <h3>Tools & Others</h3>
              <div className="tech-list">
                <span className="tech-item">GitHub</span>
                <span className="tech-item">VS Code</span>
                <span className="tech-item">Golang</span>
                <span className="tech-item">IntelliJ</span>
                <span className="tech-item">Firebase</span>
                <span className="tech-item">PostgreSQL</span>
                <span className="tech-item">GCS</span>
              </div>
            </div>
          </div>
        </section>

        {/* 2. ABOUT SECTION */}
        <section id="about" className="about-me-section">
          <h2>About Me</h2>
          <p>
            I'm currently a senior studying Computer Science at Kansas State University, but my journey 
            started in a small town called Iliff, Colorado — a quiet place of about 250 people. Growing up 
            there taught me the value of community, working hard, and staying curious. That curiosity is 
            what first drew me into tech. I found myself fascinated by everyday devices — computers, 
            phones, and apps — and I couldn't stop wondering how they worked behind the scenes.
          </p>
          <p>
            That spark turned into a passion for full-stack development, where I get to blend creative design 
            with real-world problem-solving. I enjoy building software that's not only functional but 
            meaningful — tools that make an impact. Through three internships with Home Depot, I've 
            worked on internal tools ranging from data reporting platforms to merchant systems to 
            delivery operations portals that contributed significant business value.
          </p>
          <p>
            I thrive in environments that value authenticity, creativity, and collaboration. Whether I'm 
            working on a team or coding solo, I care about delivering clean, thoughtful solutions. Outside of 
            tech, I love hiking, traveling, exploring new ideas, and spending time with my dogs. I'm also a 
            car enthusiast — if it's got a motor and some character, I'm interested.
          </p>
        </section>

        {/* 3. PROJECTS SECTION */}
        <section id="projects" className="projects-section">
          <h2 className="section-title">// FEATURED PROJECTS</h2>

          <div className="project-filters">
            {['All', 'Professional', 'School', 'Hobby'].map(filter => (
              <button
                key={filter}
                className={`filter-btn${activeFilter === filter ? ' active' : ''}`}
                onClick={() => setActiveFilter(filter)}
              >
                {filter}
              </button>
            ))}
          </div>

          <div className="project-grid">
            {filteredProjects.map(project => {
              const contentBlock = (
                <div className="project-content">
                  <div className="project-label">{project.label}</div>
                  <h3 className="project-title">{project.title}</h3>
                  <p className="project-description">{project.description}</p>
                  {project.disclosure && (
                    <p className="project-disclosure"><em>Note: Due to confidentiality agreements, I cannot discuss this project in depth or share specific implementation details.</em></p>
                  )}
                  <div className="project-tech">
                    {project.tags.map(tag => <span key={tag} className="tech-tag">{tag}</span>)}
                  </div>
                  {project.links.length > 0 && (
                    <div className="project-links">
                      {project.links.map(link => (
                        <a key={link.href} href={link.href} target="_blank" rel="noopener noreferrer" className="project-link">
                          {link.icon === 'linkedin' && (
                            <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                            </svg>
                          )}
                          {link.icon === 'github' && (
                            <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.605-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.652.242 2.873.118 3.176.77.84 1.235 1.91 1.235 3.221 0 4.609-2.807 5.623-5.479 5.921.43.372.823 1.102.823 2.222 0 1.604-.015 2.898-.015 3.293 0 .322.216.696.825.578C20.565 22.092 24 17.596 24 12.297c0-6.627-5.373-12-12-12"/>
                            </svg>
                          )}
                          {link.icon === 'external' && (
                            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                            </svg>
                          )}
                          {link.label}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              );

              const imageBlock = (
                <div className="project-image">
                  <ProjectCarousel images={project.images} />
                </div>
              );

              return (
                <div key={project.id} className={`project-card${project.featured ? ' featured' : ''}`}>
                  {project.imageRight ? <>{contentBlock}{imageBlock}</> : <>{imageBlock}{contentBlock}</>}
                </div>
              );
            })}
          </div>

          {/* Tech Headlines */}
          <div className="tech-headlines-wrapper">
            <TechNews />
          </div>
        </section>

        {/* 4. CONTACT SECTION */}
        <section id="contact" className="contact-section">
          <div className="contact-content">
            <h2 className="contact-title">Connect with Me!</h2>
            <p className="contact-description">
              My inbox is open for new opportunities! Also feel free to reach out through the 
              links displayed to talk about anything — software development, travel, hiking, or cars!
            </p>
            <button onClick={handleConnectClick} className="btn btn-contact">
              Say Hello!
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Home;